import type { Listing, ListingStatus } from '../../types';
import type { ListingFormState } from './listingForm';

const GEOJSON_TYPES = new Set([
  'FeatureCollection',
  'Feature',
  'GeometryCollection',
  'Point',
  'MultiPoint',
  'LineString',
  'MultiLineString',
  'Polygon',
  'MultiPolygon',
]);

function isGeoJsonLike(value: unknown): value is { type: string } {
  return typeof value === 'object' && value !== null && 'type' in value && typeof (value as { type: unknown }).type === 'string';
}

export function isSupportedGeoJson(value: unknown): boolean {
  return isGeoJsonLike(value) && GEOJSON_TYPES.has(value.type);
}

export async function parseGeoJsonFile(file: File): Promise<unknown> {
  const fileContents = await file.text();
  return JSON.parse(fileContents);
}

export interface RoutePlannerPoint {
  lat: string;
  lng: string;
}

function extractLineStringCoordinates(value: unknown): number[][] | null {
  if (!isGeoJsonLike(value)) return null;

  if (value.type === 'Feature') {
    return extractLineStringCoordinates((value as { geometry?: unknown }).geometry);
  }

  if (value.type === 'FeatureCollection') {
    const features = (value as { features?: unknown[] }).features ?? [];
    for (const feature of features) {
      const coordinates = extractLineStringCoordinates(feature);
      if (coordinates) return coordinates;
    }
    return null;
  }

  if (value.type === 'LineString') {
    const coordinates = (value as { coordinates?: unknown }).coordinates;
    return Array.isArray(coordinates) ? (coordinates as number[][]) : null;
  }

  if (value.type === 'MultiLineString') {
    const coordinates = (value as { coordinates?: unknown }).coordinates;
    if (!Array.isArray(coordinates) || coordinates.length === 0) return null;
    return Array.isArray(coordinates[0]) ? (coordinates[0] as number[][]) : null;
  }

  if (value.type === 'GeometryCollection') {
    const geometries = (value as { geometries?: unknown[] }).geometries ?? [];
    for (const geometry of geometries) {
      const coordinates = extractLineStringCoordinates(geometry);
      if (coordinates) return coordinates;
    }
  }

  return null;
}

export function deriveRoutePlannerPointsFromGeoJson(value: unknown): RoutePlannerPoint[] {
  const coordinates = extractLineStringCoordinates(value);
  if (!coordinates) return [];

  return coordinates
    .filter((coordinate) => Array.isArray(coordinate) && coordinate.length >= 2)
    .map((coordinate) => ({
      lat: String(coordinate[1]),
      lng: String(coordinate[0]),
    }));
}

export function createRouteGeoJsonFromPlannerPoints(points: RoutePlannerPoint[]) {
  const coordinates = points
    .map((point) => {
      const lat = Number.parseFloat(point.lat);
      const lng = Number.parseFloat(point.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) ? [lng, lat] : null;
    })
    .filter((point): point is [number, number] => Array.isArray(point));

  if (coordinates.length < 2) {
    return null;
  }

  return {
    type: 'Feature',
    properties: {
      source: 'route-planner',
    },
    geometry: {
      type: 'LineString',
      coordinates,
    },
  };
}

export async function fetchRouteGeoJsonFromPlannerPoints(points: RoutePlannerPoint[]) {
  const coordinates = points
    .map((point) => {
      const lat = Number.parseFloat(point.lat);
      const lng = Number.parseFloat(point.lng);
      return Number.isFinite(lat) && Number.isFinite(lng) ? `${lng},${lat}` : null;
    })
    .filter((point): point is string => Boolean(point));

  if (coordinates.length < 2) {
    return null;
  }

  const response = await fetch(
    `https://router.project-osrm.org/route/v1/driving/${coordinates.join(';')}?overview=full&geometries=geojson&steps=false`,
  );

  if (!response.ok) {
    throw new Error('Routing service error');
  }

  const data = await response.json();
  const geometry = data?.routes?.[0]?.geometry;

  if (!geometry || geometry.type !== 'LineString' || !Array.isArray(geometry.coordinates)) {
    throw new Error('Invalid routing response');
  }

  return {
    type: 'Feature',
    properties: {
      source: 'route-planner-auto',
    },
    geometry,
  };
}

export function createListingFormStateFromListing(listing: Listing): ListingFormState {
  return {
    title: listing.basic_information.title,
    description: listing.basic_information.description,
    listing_status: listing.listing_status,
    Dedicated: listing.Dedicated,
    order_value: listing.order_value || '',
    pilots_quantity: listing.pilots_quantity?.toString() || '1',
    permissions: listing.permissions || [],
    position_start: {
      city: listing.basic_information.position_start?.city || '',
      address: listing.basic_information.position_start?.address || '',
      lat: listing.basic_information.position_start?.lat?.toString() || '',
      lng: listing.basic_information.position_start?.lng?.toString() || '',
    },
    end_position: {
      city: listing.basic_information.end_position?.city || '',
      address: listing.basic_information.end_position?.address || '',
      lat: listing.basic_information.end_position?.lat?.toString() || '',
      lng: listing.basic_information.end_position?.lng?.toString() || '',
    },
    time_frame: {
      startDate: listing.basic_information.time_frame?.startDate
        ? new Date(listing.basic_information.time_frame.startDate).toISOString().split('T')[0]
        : '',
      endDate: listing.basic_information.time_frame?.endDate
        ? new Date(listing.basic_information.time_frame.endDate).toISOString().split('T')[0]
        : '',
    },
    route_points: deriveRoutePlannerPointsFromGeoJson(listing.basic_information.route_geojson),
    route_geojson: listing.basic_information.route_geojson || null,
  };
}

export function createListingPayload(
  formData: ListingFormState,
  options?: {
    listingStatus?: ListingStatus;
    ownerId?: number;
  }
) {
  const payload = {
    basic_information: {
      title: formData.title.trim(),
      description: formData.description.trim(),
      position_start: {
        ...formData.position_start,
        lat: formData.position_start.lat ? parseFloat(formData.position_start.lat) : undefined,
        lng: formData.position_start.lng ? parseFloat(formData.position_start.lng) : undefined,
      },
      end_position: {
        ...formData.end_position,
        lat: formData.end_position.lat ? parseFloat(formData.end_position.lat) : undefined,
        lng: formData.end_position.lng ? parseFloat(formData.end_position.lng) : undefined,
      },
      time_frame: {
        startDate: formData.time_frame.startDate ? new Date(formData.time_frame.startDate).toISOString() : undefined,
        endDate: formData.time_frame.endDate ? new Date(formData.time_frame.endDate).toISOString() : undefined,
      },
      route_geojson: formData.route_geojson,
    },
    Dedicated: formData.Dedicated,
    listing_status: options?.listingStatus ?? formData.listing_status,
    order_value: formData.order_value ? parseFloat(String(formData.order_value)) : undefined,
    pilots_quantity: Math.max(parseInt(formData.pilots_quantity, 10) || 1, 1),
    permissions: formData.permissions,
    ...(typeof options?.ownerId === 'number' ? { owner: options.ownerId } : {}),
  };

  return payload;
}
